import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Stack,
  Alert,
  Paper,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {
  getDeliveryRules,
  setDeliveryRules,
  getColourPalette,
  setColourPalette,
} from '../orders/services/configService';
import { guessHex } from '../orders/lib/colours';

const mapToRows = (map = {}) => Object.entries(map).map(([name, service]) => ({ name, service }));
const rowsToMap = (rows) =>
  rows.reduce((m, r) => {
    const key = String(r.name || '').trim().toLowerCase();
    if (key) m[key] = String(r.service || '').trim();
    return m;
  }, {});

// Module-level so typing in a field doesn't remount the inputs (focus loss).
function NameMap({ title, hint, rows, setRows }) {
  const setRow = (i, key) => (e) => setRows(rows.map((r, n) => (n === i ? { ...r, [key]: e.target.value } : r)));
  const addRow = () => setRows([...rows, { name: '', service: '' }]);
  const removeRow = (i) => setRows(rows.filter((_, n) => n !== i));
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" gutterBottom>{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{hint}</Typography>
      {rows.map((r, i) => (
        <Stack key={i} direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
          <TextField label="CSV service name" size="small" fullWidth value={r.name} onChange={setRow(i, 'name')} />
          <Box sx={{ px: 1 }}>→</Box>
          <TextField label="Maps to" size="small" fullWidth value={r.service} onChange={setRow(i, 'service')} />
          <IconButton size="small" onClick={() => removeRow(i)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={addRow}>Add mapping</Button>
    </Paper>
  );
}

/** Settings: felt colours (own save) and postage/delivery rules (collective save). */
export default function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [etsy, setEtsy] = useState({ zeroService: '', fallback: '', ladders: [] });
  const [ebayRows, setEbayRows] = useState([]);
  const [shopifyRows, setShopifyRows] = useState([]);
  const [palette, setPalette] = useState([]);
  const [newColour, setNewColour] = useState('');

  const [savingColours, setSavingColours] = useState(false);
  const [savedColours, setSavedColours] = useState(false);
  const [savingDelivery, setSavingDelivery] = useState(false);
  const [savedDelivery, setSavedDelivery] = useState(false);

  useEffect(() => {
    Promise.all([getDeliveryRules(), getColourPalette()]).then(([rules, pal]) => {
      setEtsy({
        zeroService: rules.etsy?.zeroService || '',
        fallback: rules.etsy?.fallback || '',
        ladders: (rules.etsy?.ladders || []).map((l) => ({ ...l })),
      });
      setEbayRows(mapToRows(rules.ebay?.nameMap));
      setShopifyRows(mapToRows(rules.shopify?.nameMap));
      setPalette(pal.map((c) => ({ ...c })));
      setLoading(false);
    });
  }, []);

  // ----- felt colours (index-based so names are freely editable) -----
  const setColourField = (i, field) => (e) =>
    setPalette((p) => p.map((c, n) => (n === i ? { ...c, [field]: e.target.value } : c)));
  const removeColourAt = (i) => setPalette((p) => p.filter((_, n) => n !== i));
  const addColour = () => {
    const name = newColour.trim();
    if (name) setPalette((p) => [...p, { name, hex: guessHex(name) }]);
    setNewColour('');
  };

  async function saveColours() {
    setSavingColours(true);
    setSavedColours(false);
    try {
      const cleaned = palette
        .map((c) => ({ name: String(c.name || '').trim(), hex: c.hex || guessHex(c.name) }))
        .filter((c) => c.name);
      await setColourPalette(cleaned);
      setSavedColours(true);
    } catch (e) {
      console.error('Save colours failed', e);
      window.alert(`Save failed: ${e.message}`);
    } finally {
      setSavingColours(false);
    }
  }

  // ----- postage / delivery rules -----
  const setLadder = (i, key) => (e) =>
    setEtsy((s) => ({ ...s, ladders: s.ladders.map((l, n) => (n === i ? { ...l, [key]: e.target.value } : l)) }));
  const addLadder = () =>
    setEtsy((s) => ({ ...s, ladders: [...s.ladders, { service: '', base: 0, step: 0, maxExtras: 5 }] }));
  const removeLadder = (i) => setEtsy((s) => ({ ...s, ladders: s.ladders.filter((_, n) => n !== i) }));

  async function saveDelivery() {
    setSavingDelivery(true);
    setSavedDelivery(false);
    try {
      await setDeliveryRules({
        etsy: {
          zeroService: etsy.zeroService.trim(),
          fallback: etsy.fallback.trim(),
          ladders: etsy.ladders
            .filter((l) => String(l.service).trim())
            .map((l) => ({
              service: String(l.service).trim(),
              base: Number(l.base) || 0,
              step: Number(l.step) || 0,
              maxExtras: Number(l.maxExtras) || 0,
            })),
        },
        ebay: { nameMap: rowsToMap(ebayRows) },
        shopify: { nameMap: rowsToMap(shopifyRows) },
      });
      setSavedDelivery(true);
    } catch (e) {
      console.error('Save delivery rules failed', e);
      window.alert(`Save failed: ${e.message}`);
    } finally {
      setSavingDelivery(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Loading settings…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>

      {/* ---------- Felt colours (own save) ---------- */}
      <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Felt colours</Typography>
          <Button variant="contained" onClick={saveColours} disabled={savingColours}>
            {savingColours ? 'Saving…' : 'Save felt colours'}
          </Button>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The colour of each order line is detected by matching these names in the variation text
          (used by the Workshop view). Edit the name, pick its swatch colour, and add every felt colour
          you sell. Changes apply on the next import (and immediately in the Workshop view).
        </Typography>
        {savedColours && <Alert severity="success" sx={{ mb: 2 }}>Felt colours saved.</Alert>}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 1, mb: 2 }}>
          {palette.map((c, i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.5, pl: 1 }}>
              <Box
                component="input"
                type="color"
                value={c.hex}
                onChange={setColourField(i, 'hex')}
                sx={{ width: 30, height: 30, border: 'none', background: 'none', p: 0, cursor: 'pointer', flexShrink: 0 }}
              />
              <TextField variant="standard" size="small" value={c.name} onChange={setColourField(i, 'name')} sx={{ flexGrow: 1 }} InputProps={{ disableUnderline: false }} />
              <IconButton size="small" onClick={() => removeColourAt(i)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Box>
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            label="Add colour"
            value={newColour}
            onChange={(e) => setNewColour(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addColour()}
          />
          <Button size="small" startIcon={<AddIcon />} onClick={addColour}>Add</Button>
        </Stack>
      </Paper>

      {/* ---------- Postage / delivery rules (collective save) ---------- */}
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Postage &amp; delivery rules</Typography>
        <Button variant="contained" onClick={saveDelivery} disabled={savingDelivery}>
          {savingDelivery ? 'Saving…' : 'Save postage rules'}
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        How the delivery service is decided for imported orders. Changes apply to the next import.
      </Typography>
      {savedDelivery && <Alert severity="success" sx={{ mb: 2 }}>Postage rules saved.</Alert>}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>Etsy — infer service from postage price</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Etsy exports only a postage price, so the service is inferred. Each ladder matches a price of
          <em> base + (step × extra items)</em>, up to <em>max extras</em>. Update these when the postage prices change.
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Tooltip title="Service used when postage is £0.00" arrow>
            <TextField label="Zero-price service" size="small" value={etsy.zeroService} onChange={(e) => setEtsy((s) => ({ ...s, zeroService: e.target.value }))} />
          </Tooltip>
          <Tooltip title="Service used when no ladder matches" arrow>
            <TextField label="Fallback" size="small" value={etsy.fallback} onChange={(e) => setEtsy((s) => ({ ...s, fallback: e.target.value }))} />
          </Tooltip>
        </Stack>
        {etsy.ladders.map((l, i) => (
          <Stack key={i} direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
            <TextField label="Service" size="small" sx={{ flex: 2 }} value={l.service} onChange={setLadder(i, 'service')} />
            <TextField label="Base £" type="number" size="small" sx={{ flex: 1 }} value={l.base} onChange={setLadder(i, 'base')} />
            <TextField label="Step £/item" type="number" size="small" sx={{ flex: 1 }} value={l.step} onChange={setLadder(i, 'step')} />
            <TextField label="Max extras" type="number" size="small" sx={{ flex: 1 }} value={l.maxExtras} onChange={setLadder(i, 'maxExtras')} />
            <IconButton size="small" onClick={() => removeLadder(i)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        <Button size="small" startIcon={<AddIcon />} onClick={addLadder}>Add ladder</Button>
      </Paper>

      <NameMap
        title="eBay — map the CSV 'Delivery service'"
        hint="Match on the exact eBay service name (case-insensitive) and map it to our canonical service label."
        rows={ebayRows}
        setRows={setEbayRows}
      />
      <NameMap
        title="Shopify — map the CSV 'Shipping Method'"
        hint="Match on the exact Shopify shipping method (case-insensitive) and map it to our canonical service label."
        rows={shopifyRows}
        setRows={setShopifyRows}
      />
    </Box>
  );
}
